import styles from 'css/src/components/common/comments/VideoComment.module.scss';

import React, { useCallback } from 'react';
import type Immutable from 'immutable';
import classNames from 'classnames';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import type { TextNode } from 'lexical';

import type { CommentablePersonProfile } from 'js/src/client';
import { $createMentionNode } from 'js/src/components/common/comments/MentionNode';
import {
    MentionMenuPortal,
    useEditorIsFocused,
    useScrollSelectedIntoView,
} from 'js/src/components/common/mentions/MentionTypeaheadMenu';
import { queryWords, scoreByName } from 'js/src/components/common/mentions/scoreReference';
import type { ImmutableMap } from 'js/src/libs/consts';

const getPersonDisplayName = (person: ImmutableMap<CommentablePersonProfile>): string => {
    const firstName = person.get('first_name');
    const lastName = person.get('last_name');
    if (firstName || lastName) {
        return [firstName, lastName].filter(Boolean).join(' ');
    }
    return person.get('email');
};

class MentionTypeaheadOption extends MenuOption {
    person: ImmutableMap<CommentablePersonProfile>;

    constructor(person: ImmutableMap<CommentablePersonProfile>) {
        super(getPersonDisplayName(person));
        this.person = person;
    }
}

/** Name and email score separately and the better wins, so either one finds someone. */
function scorePerson(
    person: ImmutableMap<CommentablePersonProfile>,
    words: string[],
): number | null {
    const scores = [
        scoreByName(getPersonDisplayName(person), words),
        scoreByName(person.get('email'), words),
    ].filter((score): score is number => score !== null);

    return scores.length > 0 ? Math.min(...scores) : null;
}

function MentionMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}: {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: MentionTypeaheadOption;
}) {
    const itemRef = useScrollSelectedIntoView(isSelected);

    return (
        <li
            ref={itemRef}
            key={option.person.get('id')}
            tabIndex={-1}
            className={classNames(styles['mention-menu-item'], {
                [styles['selected']]: isSelected,
            })}
            role="option"
            aria-selected={isSelected}
            id={'typeahead-item-' + index}
            onMouseEnter={onMouseEnter}
            // Keep editor focus so the blur-close doesn't beat the click.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClick}
        >
            {option.person.get('first_name') || option.person.get('last_name') ? (
                <>
                    <span className={styles['flex-40']}>{getPersonDisplayName(option.person)}</span>
                    <span className={styles['flex-60']}>{option.person.get('email')}</span>
                </>
            ) : (
                <span className={styles['flex-100']}>{option.person.get('email')}</span>
            )}
        </li>
    );
}

type Props = {
    people: Immutable.List<ImmutableMap<CommentablePersonProfile>>;
};
const MentionsPlugin: React.FC<Props> = React.memo(function MentionsPlugin({ people }) {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = React.useState<string | null>(null);
    const isFocused = useEditorIsFocused();

    const queryResults = React.useMemo(() => {
        const words = queryWords(queryString);
        const scored: { person: ImmutableMap<CommentablePersonProfile>; score: number }[] = [];
        people.forEach((person) => {
            const score = scorePerson(person, words);
            if (score !== null) {
                scored.push({ person, score });
            }
        });

        scored.sort((a, b) => a.score - b.score);
        return scored.map(({ person }) => new MentionTypeaheadOption(person));
    }, [queryString, people]);

    // `allowWhitespace` keeps the menu open past a space, so a full name matches.
    const triggerFn = useBasicTypeaheadTriggerMatch('@', {
        minLength: 0,
        allowWhitespace: true,
    });

    const onSelectOption = useCallback(
        (
            selectedOption: MentionTypeaheadOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            editor.update(() => {
                const node = $createMentionNode(selectedOption.person);
                if (nodeToReplace) {
                    nodeToReplace.replace(node);
                }
                node.select();
                closeMenu();
            });
        },
        [editor],
    );

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            options={queryResults}
            triggerFn={triggerFn}
            menuRenderFn={(
                anchorElement,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) => (
                <MentionMenuPortal
                    anchorRef={anchorElement}
                    isOpen={isFocused && queryResults.length > 0}
                    optionCount={queryResults.length}
                    className={styles['mentions-menu']}
                    testAttribute="data-mentions-menu"
                >
                    {queryResults.map((option, index) => (
                        <MentionMenuItem
                            index={index}
                            isSelected={selectedIndex === index}
                            key={option.person.get('id') || option.person.get('email')}
                            option={option}
                            onClick={() => {
                                setHighlightedIndex(index);
                                selectOptionAndCleanUp(option);
                            }}
                            onMouseEnter={() => {
                                setHighlightedIndex(index);
                            }}
                        />
                    ))}
                </MentionMenuPortal>
            )}
        />
    );
});

export default MentionsPlugin;
